import React, { useState, useEffect } from "react";
import { useBuilding } from "../../context/BuildingContext";
import { reportsApi } from "../../services/api";
import Spinner from "../../components/common/Spinner";
import ErrorAlert from "../../components/common/ErrorAlert";
import { fmt } from "./reportUtils";

interface AgedDebtorsReportProps {
  period: string;
}

const AgedDebtorsReport: React.FC<AgedDebtorsReportProps> = ({ period }) => {
  const { currentBuilding } = useBuilding();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<any>(null);

  useEffect(() => {
    if (!currentBuilding?.id) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await reportsApi.getAgedDebtors(
          currentBuilding.id,
          period,
        );
        if (!cancelled) setReport(data);
      } catch (err) {
        console.error("Error fetching aged debtors:", err);
        if (!cancelled)
          setError(
            "Failed to load aged debtors report. Please try again later.",
          );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentBuilding?.id, period]);

  if (loading) {
    return <Spinner message="Loading aged debtors report..." />;
  }

  if (error) {
    return <ErrorAlert message={error} />;
  }

  if (!report) {
    return <ErrorAlert message="No aged debtors data available" />;
  }

  const getBucketColor = (label: string) => {
    if (label === "Current") return "bg-green-50 border-l-4 border-green-500";
    if (label === "1-30 days")
      return "bg-yellow-50 border-l-4 border-yellow-500";
    if (label === "31-60 days")
      return "bg-orange-50 border-l-4 border-orange-500";
    if (label === "61-90 days")
      return "bg-orange-100 border-l-4 border-orange-600";
    return "bg-red-50 border-l-4 border-red-500";
  };

  const getBucketTextColor = (label: string) => {
    if (label === "Current") return "text-green-800";
    if (label === "1-30 days") return "text-yellow-800";
    if (label === "31-60 days") return "text-orange-800";
    if (label === "61-90 days") return "text-orange-900";
    return "text-red-800";
  };

  return (
    <div>
      <h2 className="text-xl font-semibold text-gray-900 mb-2">
        Aged Debtors Report
      </h2>
      <p className="text-sm text-gray-500 mb-6">As of {report.asOfDate}</p>

      {/* Summary Cards */}
      <div className="bg-white shadow rounded-lg mb-8 p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase">
              Total Outstanding
            </p>
            <p className="text-3xl font-bold text-red-600 mt-2">
              {fmt(report.totalOutstanding || 0)}
            </p>
            <p className="text-sm text-gray-600 mt-1">
              {report.unitCount} units with outstanding charges
            </p>
          </div>
        </div>
      </div>

      {/* Aging Buckets */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5 mb-8">
        {(report.buckets || []).map((bucket: any) => (
          <div
            key={bucket.label}
            className={`rounded-lg p-4 ${getBucketColor(bucket.label)}`}
          >
            <p
              className={`text-xs font-semibold uppercase mb-2 ${getBucketTextColor(
                bucket.label,
              )}`}
            >
              {bucket.label}
            </p>
            <p
              className={`text-2xl font-bold ${getBucketTextColor(
                bucket.label,
              )}`}
            >
              {fmt(bucket.totalAmount || 0)}
            </p>
            <p className={`text-xs mt-2 ${getBucketTextColor(bucket.label)}`}>
              {bucket.unitCount} unit{bucket.unitCount !== 1 ? "s" : ""}
            </p>
          </div>
        ))}
      </div>

      {/* Debtors Detail Table */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 border-b border-gray-200 sm:px-6">
          <h3 className="text-lg font-medium text-gray-900">
            Outstanding by Unit
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Unit
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Resident
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount Outstanding
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Days Overdue
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Bucket
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {(report.debtors || []).map((debtor: any) => (
                <tr key={debtor.unitId}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {debtor.unitName}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {debtor.residentName}
                    {debtor.residentEmail && (
                      <p className="text-xs text-gray-400">
                        {debtor.residentEmail}
                      </p>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-right text-red-600">
                    {fmt(debtor.totalOutstanding || 0)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                    {debtor.daysPastDue} days
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <span
                      className={`inline-block px-3 py-1 text-xs font-semibold rounded ${getBucketColor(
                        debtor.bucket,
                      )
                        .split("border-l-4")[0]
                        .trim()} text-gray-700`}
                    >
                      {debtor.bucket}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AgedDebtorsReport;
